import { useEffect, useMemo, useRef, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useCatalog, useRefreshAll, useShift, useStampsNeeded } from "../../hooks/queries";
import { useAutoPrint } from "../../hooks/usePrintReceipt";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { localName, money } from "../../lib/format";
import type { CreatedTransaction, SessionView } from "../../lib/types";
import { Modal } from "../ui/Modal";
import { PaymentSection, usePaymentForm } from "../ui/PaymentSection";
import { CouponPicker, couponReady, type CouponSelection } from "./CouponPicker";

interface BillView {
  at: string;
  elapsedMinutes: number;
  billableMinutes: number;
  blocks: number;
  capped: boolean;
  playPrice: number;
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  due: number;
  items: Array<{ description: string; descriptionAr: string | null; qty: number; lineTotal: number }>;
}

/**
 * Post-paid checkout. Opening it is the exit scan: the clock stops at that instant (the server holds it for 10 minutes while the
 * cashier collects), the bill is computed from the exact elapsed time by the price rule frozen at check-in, then extras, a coupon or a
 * free-stamp pass can be added before the single payment.
 */
export function PostpaidCheckoutDialog({ session, onClose }: { session: SessionView; onClose: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const { isManager } = useAuth();
  const refresh = useRefreshAll();
  const autoPrint = useAutoPrint();
  const catalog = useCatalog();
  const form = usePaymentForm();
  const shiftOpen = !!useShift().data?.shift;
  const need = useStampsNeeded();
  const [scan, setScan] = useState<{ id: string | null } | null>(null);
  const [addons, setAddons] = useState<Record<string, number>>({});
  const [coupon, setCoupon] = useState<CouponSelection | null>(null);
  const [redeem, setRedeem] = useState(false);
  const [busy, setBusy] = useState(false);

  // The scan: stops the clock. A double beep (duplicate) simply continues with the live clock.
  const request = useRef<Promise<{ action: string; scanEventId?: string }> | null>(null);
  useEffect(() => {
    let live = true;
    // one scan per dialog, even if React mounts the effect twice
    request.current ??= api.post<{ action: string; scanEventId?: string }>("/sessions/scan-exit", { code: session.wristbandTag, source: "manual_entry" });
    request.current
      .then((r) => { if (live) setScan({ id: r.scanEventId ?? null }); })
      .catch((e) => { if (live) { toastError(e); onClose(); } });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  const extras = useMemo(() => (catalog.data ?? []).filter((p) => ["addon", "fnb", "merch", "combo"].includes(p.kind)), [catalog.data]);
  const addonList = useMemo(() => Object.entries(addons).filter(([, q]) => q > 0).map(([productId, qty]) => ({ productId, qty })), [addons]);
  const extrasBody = useMemo(() => ({
    addons: addonList,
    redeemStamps: redeem,
    coupon: coupon && !redeem ? { code: coupon.rule.code, note: coupon.note.trim() || undefined, overrideId: coupon.overrideId } : undefined,
  }), [addonList, redeem, coupon]);

  const bill = useQuery({
    queryKey: ["bill", session.id, scan?.id ?? "live", extrasBody],
    enabled: !!scan,
    placeholderData: keepPreviousData,
    refetchInterval: scan?.id ? false : 15_000,
    queryFn: () => api.post<BillView>(`/sessions/${session.id}/bill`, { scanEventId: scan?.id ?? undefined, extras: extrasBody }),
  });
  const b = bill.data;
  const due = b?.due ?? 0;
  // cash goes through the drawer: it needs an open shift (the server refuses otherwise)
  const needsShift = due > 0 && form.method === "cash" && !shiftOpen;

  async function submit() {
    if (!b) return;
    setBusy(true);
    try {
      const res = await api.post<{ transaction: CreatedTransaction | null; freeStampAvailable: boolean }>(`/sessions/${session.id}/checkout`, {
        scanEventId: scan?.id ?? undefined, extras: extrasBody, payments: form.payments(due), waiveOvertime: false,
      });
      toast(res.freeStampAvailable ? t("checkout.doneFree", { name: session.kid.name }) : t("checkout.done", { name: session.kid.name }), "success");
      if (res.transaction) autoPrint(res.transaction.id);
      refresh();
      onClose();
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  }

  const hms = (m: number) => `${Math.floor(m / 60)}${t("common.hourShort")} ${String(m % 60).padStart(2, "0")}${t("common.min")}`;

  return (
    <Modal
      open
      onClose={onClose}
      title={t("postpaid.title", { name: session.kid.name })}
      size="md"
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button>
          <button type="button" className="btn-dark" disabled={busy || !b || bill.isFetching || needsShift || !form.isValid(due) || !couponReady(coupon, isManager)} onClick={submit}>
            {due > 0 ? t("checkout.collectAndExit", { amount: money(due) }) : t("checkout.exit")}
          </button>
        </>
      }
    >
      <div className="rounded-xl bg-gray-50 p-3 text-xs font-bold">
        <div className="flex justify-between"><span>{t("table.tag")}</span><span className="num font-mono">{session.wristbandTag}</span></div>
        <div className="flex justify-between"><span>{t("table.parent")}</span><span>{session.guardian.fullName}</span></div>
        <div className="flex justify-between"><span>{t("loyalty.stamps")}</span><span className="num">{session.kid.stamps} / {need}</span></div>
      </div>

      {!b ? (
        <p className="text-xs font-bold text-gray-500">{t("common.loading")}</p>
      ) : (
        <>
          <div className="rounded-xl border border-purple-200 bg-purple-50/60 p-3" data-testid="postpaid-bill">
            <div className="flex items-baseline justify-between">
              <span className="text-xs font-black text-gray-600">{t("postpaid.elapsed")}</span>
              <span className="num font-mono text-lg font-black text-cosmic-navy">{hms(b.elapsedMinutes)}</span>
            </div>
            <div className="mt-1 flex items-baseline justify-between text-xs font-bold text-gray-600">
              <span>{t("postpaid.blocks", { count: b.blocks })}{b.capped ? ` · ${t("postpaid.capped")}` : ""}</span>
              <span className="num font-mono">{money(b.playPrice)} {t("currency")}</span>
            </div>
            <p className="mt-1 text-[10px] font-semibold text-gray-500">{scan?.id ? t("postpaid.clockStopped") : t("postpaid.clockLive")}</p>
          </div>

          <fieldset className="space-y-1.5">
            <legend className="label">{t("checkin.extras")}</legend>
            {extras.slice(0, 8).map((p) => {
              const qty = addons[p.id] ?? 0;
              const out = p.tracksStock && (p.qtyOnHand ?? 0) <= 0;
              return (
                <div key={p.id} className="flex items-center justify-between gap-2 text-xs font-bold text-gray-700">
                  <span className={out ? "text-gray-300 line-through" : ""}>{localName(p, locale)} <span className="num text-gray-400">+{money(p.price)}</span></span>
                  <span className="flex items-center gap-1">
                    <button type="button" className="btn-ghost !px-2 !py-0.5" disabled={qty === 0} onClick={() => setAddons((a) => ({ ...a, [p.id]: Math.max(0, qty - 1) }))} aria-label={`− ${localName(p, locale)}`}>−</button>
                    <span className="num w-4 text-center">{qty}</span>
                    <button type="button" className="btn-ghost !px-2 !py-0.5" disabled={out} onClick={() => setAddons((a) => ({ ...a, [p.id]: qty + 1 }))} aria-label={`+ ${localName(p, locale)}`}>+</button>
                  </span>
                </div>
              );
            })}
          </fieldset>

          {session.productId && (
            <CouponPicker guardianId={session.guardian.id} kidId={session.kid.id} passProductId={session.productId} addons={addonList} sessionId={session.id} disabled={redeem} value={coupon} onChange={setCoupon} />
          )}
          {session.freeStampAvailable && (
            <label className="flex items-center gap-2 rounded-xl border border-amber-300 bg-amber-50 p-2.5 text-xs font-black text-amber-900">
              <input type="checkbox" className="rounded text-cosmic-purple focus:ring-0" checked={redeem} onChange={(e) => { setRedeem(e.target.checked); if (e.target.checked) setCoupon(null); }} />
              🎁 {t("checkin.redeem", { name: session.kid.name })}
            </label>
          )}

          <div className="space-y-1 rounded-xl border border-gray-200 p-3 text-xs font-bold text-gray-700">
            {b.items.map((i, k) => (
              <div key={k} className="flex justify-between gap-2"><span>{(locale === "ar" && i.descriptionAr) || i.description}{i.qty > 1 ? ` ×${i.qty}` : ""}</span><span className="num font-mono">{money(i.lineTotal)}</span></div>
            ))}
            {b.discount > 0 && <div className="flex justify-between text-emerald-700"><span>{t("payment.discount")}</span><span className="num font-mono">−{money(b.discount)}</span></div>}
            <div className="flex justify-between text-gray-500"><span>{t("payment.tax")}</span><span className="num font-mono">{money(b.tax)}</span></div>
            <div className="flex items-baseline justify-between border-t border-gray-100 pt-1.5 text-sm font-black text-cosmic-navy">
              <span>{t("payment.total")}</span>
              <span className="num font-mono text-cosmic-purple">{money(due)} {t("currency")}</span>
            </div>
          </div>
          <PaymentSection form={form} total={due} />
          {needsShift && <p className="text-[11px] font-bold text-amber-700">⚠️ {t("shift.requiredHint")}</p>}
        </>
      )}
    </Modal>
  );
}
